import React, { FC, useState } from 'react';
import { useTranslate } from '@/translations';
import { App, Input, Modal, Spin, Switch } from 'antd';
import { CustomCard } from '@/ui/components';
import { useNavigate } from 'react-router-dom';
import {
  useUpdateInspectionsVehicle,
  useUpdateInspectionVehicleDamageDefaultStatus,
  refreshInspectionIfStatusChanged,
} from '@/api/Inspections';
import { AppRoutes } from '@/routes';
import { InspectionStatusMap, SelectOption } from '@/types';

interface InspectionStatusComponentProps {
  inspectionId: string;
  nextStatusList: SelectOption<number>[];
  isPopupVisible: boolean;
  onTogglePopup: () => void;
}
export const InspectionStatusComponent: FC<InspectionStatusComponentProps> = ({
  inspectionId,
  nextStatusList,
  isPopupVisible,
  onTogglePopup,
}) => {
  const [selectedId, setSelectedId] = useState<number>();
  const [authRejectedComment, setAuthRejectedComment] = useState('');
  const [pendingAuthRejectedStatus, setPendingAuthRejectedStatus] = useState<{
    id: number;
    text: InspectionStatusMap;
  }>();
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { message, modal, notification } = App.useApp();
  const { mutateAsync: updateMutateAsync, isPending: updateStatusLoading } =
    useUpdateInspectionsVehicle(Number(inspectionId));

  const { mutateAsync: approveMutateAsync } =
    useUpdateInspectionVehicleDamageDefaultStatus();

  const onChangeStatus = async (
    statusId: number,
    statusText: InspectionStatusMap,
    forceChange?: boolean,
    comment?: string,
  ) => {
    const res = await updateMutateAsync({
      id: Number(inspectionId),
      inspectionStatus: statusId,
      forceChange: forceChange || false,
      authorizationNotes: comment,
    });
    const hasConflict = res.code === 409;
    console.log(res);
    if (hasConflict) {
      const conflictType = res.message?.Message;
      const conflictData = res.message?.Data;
      const isCodesConflict = conflictType == 'codes';
      const isApprovalConflict = conflictType == 'approval';
      if (isCodesConflict || isApprovalConflict) {
        modal.confirm({
          title: t('confirm'),
          content: (
            <>
              {isCodesConflict
                ? 'The damage lines listed below do not have any FR codes or parts added.'
                : 'Some damage lines require approval from the sub-customer.'}
              {conflictData.map((o: any) => (
                <div className="my-1">
                  {o.Id}: {o.DamageAreaDesc}-{o.DamagePartDesc}-
                  {o.RepairActionDesc}
                </div>
              ))}
            </>
          ),
          okText: 'Continue',
          closable: isCodesConflict ? undefined : true,
          footer: isCodesConflict ? undefined : null,
          onOk: () => onChangeStatus(statusId, statusText, true),
        });
        setSelectedId(undefined);
      } else if (conflictType == 'status') {
        modal.confirm({
          title: t('confirm'),
          content:
            'Some of the damage lines have not been approved or rejected. Please choose how to proceed.',
          okText: 'Approve all default damage lines',
          cancelText: 'I’ll review and update them manually',
          width: '50vw',
          onOk: async () => {
            const approveRes = await approveMutateAsync(Number(inspectionId));
            if (approveRes.success) {
              refreshInspectionIfStatusChanged(
                Number(inspectionId),
                approveRes.data,
              );
              onChangeStatus(statusId, statusText);
            } else {
              notification.error({
                message: 'Error',
                description: 'Failed to approve damage lines.',
              });
            }
          },
          onCancel: () => {
            setSelectedId(undefined);
            onTogglePopup();
          },
        });
      }
    } else if (!res.success) {
      message.error(res.message.Message, 10);
      setSelectedId(undefined);
    } else {
      const changeResult = (res.data?.entities || [])?.[0];
      const success = changeResult?.result === 'OK';
      if (success) {
        onTogglePopup();

        if (
          [
            InspectionStatusMap.Authorised,
            InspectionStatusMap.RequiresReSubmissionReview,
          ].includes(statusText)
        ) {
          notification.success({
            message: 'Update inspection status success',
            description: 'Will back to inspections page soon.',
            duration: 3,
            onClose: () => navigate(AppRoutes.inspections),
          });
        }
      } else {
        message.error(changeResult?.message, 10);
        setSelectedId(undefined);
      }
    }
  };

  return (
    <>
      <Modal
        open={isPopupVisible}
        onCancel={onTogglePopup}
        title={t('changeInspectionStatus')}
        maskClosable
        footer={null}
        centered
      >
        <Spin spinning={updateStatusLoading}>
          {nextStatusList.length > 0 ? (
            <div className="d-flex flex-column gap-3">
              {nextStatusList.map((o) => (
                <CustomCard
                  key={o.value}
                  direction="row"
                  className="justify-content-between align-items-center"
                >
                  <span>{o.text}</span>
                  <Switch
                    checked={selectedId === o.value}
                    onChange={() => {
                      setSelectedId(o.value);
                      if (o.text === InspectionStatusMap.AuthRejected) {
                        setPendingAuthRejectedStatus({
                          id: o.value,
                          text: o.text,
                        });
                        setAuthRejectedComment('');
                        return;
                      } else {
                        onChangeStatus(o.value, o.text as InspectionStatusMap);
                      }
                    }}
                  />
                </CustomCard>
              ))}
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center py-4">
              <span>No status available</span>
            </div>
          )}
        </Spin>
      </Modal>

      <Modal
        open={pendingAuthRejectedStatus !== undefined}
        title={t('authorizationNotes')}
        onCancel={() => {
          setPendingAuthRejectedStatus(undefined);
          setAuthRejectedComment('');
          setSelectedId(undefined);
        }}
        centered
        destroyOnHidden
        okText={t('yes')}
        cancelText={t('cancel')}
        okButtonProps={{ disabled: !authRejectedComment.trim() }}
        onOk={async () => {
          const normalizedComment = authRejectedComment.trim();
          if (!normalizedComment) return;
          if (!pendingAuthRejectedStatus) return;

          await onChangeStatus(
            pendingAuthRejectedStatus.id,
            pendingAuthRejectedStatus.text,
            false,
            normalizedComment,
          );
          setPendingAuthRejectedStatus(undefined);
          setAuthRejectedComment('');
        }}
      >
        <Input.TextArea
          rows={4}
          value={authRejectedComment}
          onChange={(e) => setAuthRejectedComment(e.target.value)}
          placeholder={t('addNote')}
          required
        />
      </Modal>
    </>
  );
};
